import styled from "styled-components";
import CartProduct from "../../store/cart-product/model";
import CartItem from "../cart/CartItem";
import {
  getContentfulProductForPlan,
  getPlanForId,
} from "../../utils/planToProduct";
import Text from "../Text";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import cartProductSelectors from "../../store/cart-product/selectors";
import ProductPromotionCartItems from "../cart/ProductPromotionCartItems";
import Product from "../../store/product/model";

const CartItemsList = styled.div`
  transition: height 0.3s ease;
  overflow: hidden;
`;

const CadenceTitle = styled.h3<{ $topMargin?: boolean }>`
  position: relative;
  z-index: 10;
  margin: -1px 0 0 0;
  color: var(--indigo-blue-60);
  background-color: var(--white);
  display: flex;
  align-items: center;
  margin-top: ${(props) => (props.$topMargin ? "var(--spacing-0_5)" : "-1px")};

  span.title-text {
    flex: 0 0 auto;
    padding-right: var(--spacing-0_5);
  }

  span.title-line {
    width: 100%;
    flex: 1 1 auto;
    border-top: 1px solid var(--cool-40);
  }
`;

interface CadenceGroupProps {
  intervalCount: number;
  items: (CartProduct & { intervalCount: number })[];
  isProcessing?: boolean;
  cartQuantity: number;
  cartItemOptions: any;
  topMargin: boolean;
}

const CadenceGroup = ({
  intervalCount,
  items,
  isProcessing,
  cartQuantity,
  cartItemOptions,
  topMargin,
}: CadenceGroupProps) => {
  return (
    <>
      <CadenceTitle
        key={`title-${intervalCount}`}
        className="typography-caption"
        $topMargin={topMargin}
      >
        <span className="title-text">
          <Text
            id={`checkout.cart-ledger.cadence-title-${intervalCount}`}
            defaultMessage=""
          />
        </span>
        <span className="title-line" />
      </CadenceTitle>
      {items.map((item, i) => {
        const contentfulProduct = getContentfulProductForPlan(item?.planId);
        const lastItem = i === items.length - 1;
        return (
          <CartItem
            key={item.key}
            className={`simplified ${lastItem ? "last-item" : ""}`}
            cartProduct={item}
            contentfulProduct={contentfulProduct}
            isProcessing={isProcessing}
            cartQuantity={cartQuantity}
            simplified={true}
            isFlyoutContext={false}
            hideDeleteButton={cartItemOptions.hideDeleteButton}
            disableQuantitySelector={cartItemOptions.disableQuantitySelector}
          />
        );
      })}
    </>
  );
};

interface LedgerItemsProps {
  activeCartProducts: CartProduct[];
  collapsed?: boolean;
  disableAnimation?: boolean;
  isProcessing?: boolean;
  cartItemOptions?: any;
  appliedProductPromotionProductsOverride?: Product[];
}

const getIntervalCount = (cartProduct: CartProduct) => {
  let intervalCount;
  if (cartProduct?.planId) {
    const plan = getPlanForId(cartProduct.planId);
    if (!plan) return null;
    intervalCount = plan.intervalCount || 0;
  }
  if (cartProduct?.productOfferId) {
    // TODO: Interval Count can be assumed 30 for now
    intervalCount = 30;
  }
  return intervalCount;
};

const LedgerItems = ({
  activeCartProducts,
  collapsed,
  disableAnimation,
  isProcessing,
  cartItemOptions = {
    hideDeleteButton: true,
    disableQuantitySelector: true,
  },
  appliedProductPromotionProductsOverride,
}: LedgerItemsProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [cartProducts, setCartProducts] = useState<CartProduct[]>([]);
  const [finalCount, setFinalCount] = useState<number | null>(null);

  const cartQuantity = useSelector(
    cartProductSelectors.activeCartProductQuantity,
  );

  useEffect(() => {
    if (activeCartProducts.length && activeCartProducts.length === finalCount) {
      setCartProducts(activeCartProducts);
    }
  }, [activeCartProducts, finalCount]);

  useEffect(() => {
    const stabilizeData = () => {
      const currentCount = activeCartProducts.length;
      if (currentCount > 0 && currentCount !== finalCount) {
        setFinalCount(currentCount);
      }
    };

    stabilizeData();
  }, [activeCartProducts, finalCount]);

  if (!cartProducts || cartProducts.length < 1) return null;

  const groupedByIntervalCount = cartProducts.reduce(
    (acc, cartProduct) => {
      const intervalCount = getIntervalCount(cartProduct) ?? 0;
      if (!acc[intervalCount]) {
        acc[intervalCount] = [];
      }
      // Add intervalCount property to the existing cartProduct
      (cartProduct as any).intervalCount = intervalCount;
      acc[intervalCount].push(
        cartProduct as CartProduct & { intervalCount: number },
      );
      return acc;
    },
    {} as Record<number, (CartProduct & { intervalCount: number })[]>,
  );

  // Get sorted interval counts
  const sortedIntervalCounts = Object.keys(groupedByIntervalCount)
    .map(Number)
    .sort((a, b) => a - b);

  const height = collapsed
    ? "0px"
    : `${contentRef?.current?.scrollHeight || 1}px`;

  return (
    <CartItemsList
      ref={contentRef}
      style={{
        height: disableAnimation ? "auto" : height,
      }}
    >
      {sortedIntervalCounts.map((intervalCount, i) => {
        const items = groupedByIntervalCount[intervalCount];

        if (!items || items.length === 0) return null;

        return (
          <CadenceGroup
            key={intervalCount}
            intervalCount={intervalCount}
            items={items}
            isProcessing={isProcessing}
            cartQuantity={cartQuantity}
            cartItemOptions={cartItemOptions}
            topMargin={i > 0}
          />
        );
      })}
      <ProductPromotionCartItems
        isFlyoutContext={false}
        simplified
        key="gift-with-purchase"
        appliedProductPromotionProductsOverride={
          appliedProductPromotionProductsOverride
        }
        topMargin={sortedIntervalCounts.length > 0}
      />
      <div></div>
    </CartItemsList>
  );
};

export default LedgerItems;
